import { LockOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import SharpIcon from '@/assets/icons/SharpIcon';
import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import RadioButton from '@/components/Common/RadioButton';
import ScrollView from '@/components/Common/ScrollView';
import Spinner from '@/components/Common/Spinner';
import ToggleRadio from '@/components/Common/ToggleRadio';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import { AttrText, MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import type {
  SlackChannelT,
  SlackWorkspaceT,
} from '@/features/Setting/Integrations/providers/types';
import useColor from '@/hooks/domain/useColor';
import useFetch from '@/hooks/pure/useFetch';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import { modalStyle } from '@/styles/style';

type SlackIntegrationConfigModalProps = {
  workspace?: SlackWorkspaceT;
};

const SlackIntegrationConfigModal = (
  props: SlackIntegrationConfigModalProps,
) => {
  const color = useColor();
  const { closeModal } = useModalContext();
  const [channelList, setChannelList] = useState<SlackChannelT[] | null>(null);

  const toggleChannel = async (
    channelId: string,
    status: boolean,
    isPrivate: boolean,
  ) => {
    if (!channelList) return;
    if (!props.workspace) return;
    const { id: teamId } = props.workspace;

    if (status) {
      await service.slack.joinSlackChannel(teamId, channelId, isPrivate);
    } else {
      await service.slack.leaveSlackChannel(teamId, channelId, isPrivate);
    }
    const targetIndex = channelList.findIndex(
      (channel) => channel.channel_id === channelId,
    );
    const newChannel = {
      ...channelList[targetIndex],
      is_member: status,
    };
    const newList = [
      ...channelList.slice(0, targetIndex),
      newChannel,
      ...channelList.slice(targetIndex + 1),
    ];
    setChannelList(newList);
  };

  const setCollectMethod = async (
    channelId: string,
    is_auto: boolean,
    isPrivate: boolean,
  ) => {
    if (!channelList) return;
    if (!props.workspace) return;
    const { id: teamId } = props.workspace;

    if (is_auto) {
      await service.slack.setAutoCollect(teamId, channelId, isPrivate);
    } else {
      await service.slack.setManualCollect(teamId, channelId, isPrivate);
    }
    const targetIndex = channelList.findIndex(
      (channel) => channel.channel_id === channelId,
    );
    const newChannel = {
      ...channelList[targetIndex],
      is_auto,
    };
    const newList = [
      ...channelList.slice(0, targetIndex),
      newChannel,
      ...channelList.slice(targetIndex + 1),
    ];
    setChannelList(newList);
  };

  const fetchData = useCallback(async () => {
    if (!props.workspace) return;
    const { id: teamId } = props.workspace;

    const list = await service.slack.fetchSlackChannelList(teamId);
    setChannelList(list);
  }, [props.workspace]);

  useFetch(fetchData);

  return (
    <ScrollView style={{ maxHeight: 'calc(100vh - 80px' }}>
      <Style d="column">
        <Heading type="h2" color={color.gray800}>
          {props.workspace ? props.workspace.name : ''}
        </Heading>
        <Space h="4px" />
        <Typo type="label2" color={color.gray700}>
          Select channels where your team would use Syncly
        </Typo>
        <Space h="32px" />
        <Flex className="channel-list-wrapper">
          {channelList ? (
            <ScrollView style={{ width: '100%' }}>
              <Flex
                g="8px"
                j="space-between"
                style={{ flexWrap: 'wrap', width: '100%', padding: 24 }}
              >
                {channelList.map((channelItem) => (
                  <ChannelListItem
                    key={channelItem.channel_id}
                    {...channelItem}
                    toggleChannel={toggleChannel}
                    setCollectMethod={setCollectMethod}
                  />
                ))}
              </Flex>
            </ScrollView>
          ) : (
            <Flex
              a="center"
              j="center"
              style={{ width: '100%', height: '100%' }}
              d="column"
              g="16px"
            >
              <Spinner />
              <Typo type="label2" color={color.gray500}>
                Loading Channel list...
              </Typo>
            </Flex>
          )}
        </Flex>
        <Space h="24px" />
        <GuideWrapper>
          <Typo type="label2" weight="sb">
            Manual Push Guide
          </Typo>
          <Space h="16px" />
          <Typo type="label2" weight="m">
            1. Download and add to your slack workspace
          </Typo>
          <Space h="4px" />
          <IndentWrapper>
            <Typo type="body2">
              This emoji triggers the push action.
              <br />
              Make sure to maintain the emoji name{' '}
              <strong>:push_syncly:</strong> as this is the magic word!
            </Typo>
            <Space h="16px" />
            <Flex g="16px" a="flex-end">
              <img
                src="/assets/push_syncly.png"
                width={48}
                height={48}
                alt="push_syncly"
              />
              <a href="/assets/push_syncly.png" download>
                <Button variant="outlined">Download</Button>
              </a>
            </Flex>
          </IndentWrapper>
          <Space h="32px" />
          <Typo type="label2" weight="m">
            2. Add it to your workspace
          </Typo>
          <Space h="4px" />
          <IndentWrapper>
            <Typo
              type="body2"
              color={color.primary100}
              style={{ textDecoration: 'underline' }}
            >
              <a
                href={import.meta.env.VITE_PUBLIC_ADD_EMOJI_TO_SLACK_URL}
                target="_blank"
                rel="noreferrer"
              >
                How to add emoji to slack
              </a>
            </Typo>
          </IndentWrapper>
        </GuideWrapper>
        <Space h="16px" />
        <Flex j="flex-end">
          <Button sizeType="m" onClick={closeModal}>
            Done
          </Button>
        </Flex>
      </Style>
    </ScrollView>
  );
};

const GuideWrapper = styled.div`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.ND02};
  border: 1px solid ${(props) => props.theme.colors.gray300};
  border-radius: 8px;
`;

const IndentWrapper = styled.div`
  padding-left: 16px;
`;

const Style = styled(Flex)`
  padding: 24px;
  width: 800px;
  ${modalStyle};
  .channel-list-wrapper {
    height: 400px;
    border: 1px solid ${(props) => props.theme.colors.gray300};
    border-radius: 8px;
  }
`;

type ItemProps = SlackChannelT & {
  toggleChannel: (
    channelId: string,
    status: boolean,
    isPrivate: boolean,
  ) => void;
  setCollectMethod: (
    channelId: string,
    is_auto: boolean,
    isPrivate: boolean,
  ) => void;
};

const ChannelListItem = (props: ItemProps) => {
  const Icon = useMemo(() => {
    return props.is_private ? <LockOutlined /> : <SharpIcon />;
  }, [props.is_private]);

  const setMethod = async (status: boolean) => {
    try {
      setValue('loading', true);
      setValue('isAuto', status);
      await props.setCollectMethod(
        props.channel_id,
        status,
        props.is_private || false,
      );
      setValue('loading', false);
    } catch (e) {
      setValue('loading', false);
      setValue('isAuto', !status);
    }
  };

  const toggleChannel = async (status: boolean) => {
    try {
      setValue('loading', true);
      setValue('isActive', status);
      await props.toggleChannel(
        props.channel_id,
        status,
        props.is_private || false,
      );
      setValue('loading', false);
    } catch (e) {
      setValue('loading', false);
      setValue('isActive', !status);
    }
  };

  const { state, setValue } = useStates({
    isActive: props.is_member || false,
    isAuto: props.is_auto || false,
    loading: false,
  });

  // const isActive = useMemo(() => {
  //   return props.is_member || false;
  // }, [props.is_member]);

  // const isAuto = useMemo(() => {
  //   return props.is_auto || false;
  // }, [props.is_auto]);

  return (
    <ItemStyle d="column" style={{ width: '45%' }} g="8px">
      <Flex j="space-between">
        <Flex g="4px" a="center">
          {Icon}
          <Typo type="label2">{props.name}</Typo>
          {state.loading && <Spinner size={16} />}
        </Flex>
        <ToggleRadio status={state.isActive} onChange={toggleChannel} />
      </Flex>
      {props.is_member && (
        <Flex j="space-between" a="center">
          <AttrText>Collect type</AttrText>
          <Flex g="16px" a="center">
            <Flex a="center" g="8px">
              <MainText>Auto</MainText>
              <RadioButton status={state.isAuto} onClick={setMethod} />
            </Flex>
            <Flex a="center" g="8px">
              <MainText>Manual</MainText>
              <RadioButton
                status={!state.isAuto}
                onClick={(status) => setMethod(!status)}
              />
            </Flex>
          </Flex>
        </Flex>
      )}
    </ItemStyle>
  );
};

const ItemStyle = styled(Flex)`
  padding: 16px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.gray300};
`;

export default SlackIntegrationConfigModal;
